import { BodyText } from '@epilot/base-elements'
import React, {
  ChangeEvent,
  CSSProperties,
  memo,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useClickAway, useDebounce, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import {
  DELETED_TRANSLATION,
  TranslationContext,
  TranslationContextState
} from 'context/TranslationContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import { isConfigMode } from 'utils/StyleUtils'

import PortalConfigContext from '../../../context/PortalConfigContext'
import { getCurrentLanguage } from '../../../utils/LanguageUtils'
import {
  RemoveTranslation,
  ResetTranslation
} from '../editable/TranslationActionButton'
import PreviewModeLink from '../preview/PreviewModeLink'

import EditableTextarea from './EditableTextarea'
import Text, { TEXT_TYPE } from './Text'

interface Props {
  translationKey: string
  translationOptions?: any
  type?: TEXT_TYPE
  className?: string
  style?: CSSProperties
  url?: string
  removable?: boolean
  hasReset?: boolean
}

const Translator = ({
  translationKey,
  translationOptions = null,
  type = TEXT_TYPE.BODY,
  url,
  style,
  removable,
  hasReset = true,
  ...restProps
}: Props) => {
  const { t } = useEcpTranslation()
  const { t: defaultTranslations } = useTranslation()
  const ref = useRef(null)

  const [isInEditMode, setIsInEditMode] = useState<boolean>(false)
  const [value, setValue] = useState<string>(
    t(translationKey, translationOptions)
  )
  const { actions: translationActions } =
    useContext<TranslationContextState>(TranslationContext)

  const { data: portalConfigData } = useContext(PortalConfigContext)

  const defaultValue: string = defaultTranslations(
    translationKey,
    translationOptions
  )

  const isTextChanged: boolean = useMemo(
    () => defaultValue !== value,
    [defaultValue, value]
  )

  useUpdateEffect(() => {
    if (value !== '') {
      setValue(t(translationKey, translationOptions))
    }
  }, [translationKey, translationOptions])

  const [cancel] = useDebounce(
    () => {
      if (isInEditMode) {
        // if the translation is empty and the key is a welcome banner key,
        // don't update the translation because it will remove the bullet from DOM
        if (translationKey.includes('welcome_banner') && !value) {
          return
        }
        translationActions.updateTranslation(translationKey, value)
      }
    },
    200,
    [value]
  )

  useClickAway(ref, () => {
    cancel()
    setIsInEditMode(false)

    if (!value) {
      setValue(t(translationKey, translationOptions))
    }
  })

  const enableEditMode = () => {
    setIsInEditMode(true)
  }

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value)
  }

  const resetToDefaultValue = (): void => {
    const previousTranslation: string =
      portalConfigData.originalConfig.config.translationOverrides?.[
        getCurrentLanguage()
      ]?.[translationKey]

    setValue(previousTranslation)

    translationActions.updateTranslation(
      translationKey,
      defaultValue || previousTranslation
    )
  }

  const removeTranslation = () => {
    translationActions.removeTranslation(translationKey)
  }

  if (t(translationKey, translationOptions) === DELETED_TRANSLATION) {
    return null
  }

  const getContactHref = () => {
    const contactType = translationKey.includes('help_phone') ? `tel` : 'mailto'

    return `${contactType}:${t(translationKey, translationOptions)}`
  }

  if (!isConfigMode()) {
    const isContactSupport =
      translationKey.includes('help_phone') ||
      translationKey.includes('help_email')

    return (
      <Text {...restProps} type={type}>
        {url ? (
          <PreviewModeLink to={url}>
            {t(translationKey, translationOptions)}
          </PreviewModeLink>
        ) : isContactSupport ? (
          <a href={getContactHref()} {...restProps}>
            {t(translationKey, translationOptions)}
          </a>
        ) : (
          t(translationKey, translationOptions)
        )}
      </Text>
    )
  }

  const welcomeBannerBulletTranslationProxy = () => {
    const isWelcomeBannerBullet: boolean =
      translationKey.includes('welcome_banner')

    if (
      (isWelcomeBannerBullet && value.includes('welcome_banner.login.')) ||
      value.includes('welcome_banner.register.')
    ) {
      return 'Lorem ipsum dolor sit amet'
    }

    return value
  }

  if (isInEditMode) {
    return (
      <Container $active ref={ref} style={style}>
        <Header>
          <OptionsContainer>
            <TypeText>{url ? 'Link' : 'Text'}</TypeText>
          </OptionsContainer>
        </Header>

        <Content>
          <EditableTextarea
            {...restProps}
            $type={type}
            onChange={onChange}
            value={welcomeBannerBulletTranslationProxy()}
          />
        </Content>
      </Container>
    )
  }

  return (
    <Container ref={ref} style={style}>
      <Header>
        <OptionsContainer>
          <TypeText>{url ? 'Link' : 'Text'}</TypeText>
        </OptionsContainer>

        <ActionsContainer>
          {removable && <RemoveTranslation onClick={removeTranslation} />}

          {hasReset && isTextChanged && (
            <ResetTranslation onClick={resetToDefaultValue} />
          )}
        </ActionsContainer>
      </Header>

      <Content onClick={enableEditMode}>
        <Text {...restProps} type={type}>
          {url ? (
            <PreviewModeLink to={url}>
              {welcomeBannerBulletTranslationProxy()}
            </PreviewModeLink>
          ) : (
            welcomeBannerBulletTranslationProxy()
          )}
        </Text>
      </Content>
    </Container>
  )
}

export default memo(Translator)

const Header = styled.div`
  position: absolute;
  top: -26px;
  left: 0;
  height: 24px;
  width: fit-content;
  display: none;
  justify-content: space-between;
`

const ResetIconContainer = styled.div`
  background: #ffffff;
  box-shadow: 0 0.1rem 0.4rem rgba(21, 43, 66, 0.16);
  border-bottom-right-radius: 0.4rem;
  border-top-right-radius: 0.4rem;
  padding: 1rem 0.2rem;
  height: 3.4rem;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 0;
  right: -20px;
  z-index: -1;
  display: none;
`

const ActionsContainer = styled.div`
  display: flex;

  > div:not(:first-child) {
    margin-left: 0.2rem;
  }
`

const Container = styled.div<{ $active?: boolean }>`
  width: auto;
  position: relative;
  padding: 0 2px;
  margin: -2px -4px;
  display: flex;
  flex-direction: column;
  margin: -1.5px;
  border: 1.5px solid
    ${({ theme, $active }) =>
      $active ? theme.palette.primary[400] : 'transparent'};

  ${Header} {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
  }

  :hover {
    border: 1.5px solid ${({ theme }) => theme.palette.primary[400]};

    ${Header}, ${ResetIconContainer} {
      display: flex;
    }
  }
`

const OptionsContainer = styled.div`
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(21, 43, 66, 0.16);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  padding: 0 5px;
  height: 24px;
  display: flex;
  align-items: center;
`

const TypeText = styled(BodyText)`
  font-weight: 600;
`

const Content = styled.div`
  display: flex;
  cursor: pointer;

  > * {
    width: 100%;
  }
`
